import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { RotateCcw, X } from "lucide-react";

type FilterPanelProps = {
  open: boolean;
  title: string;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  onReset: () => void;
  onApply: () => void;
  children: ReactNode;
  applyLabel?: string;
};

type DesktopPosition = {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
};

export function FilterPanel({
  open,
  title,
  triggerRef,
  onClose,
  onReset,
  onApply,
  children,
  applyLabel = "Apply filters",
}: FilterPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [desktopPosition, setDesktopPosition] =
    useState<DesktopPosition | null>(null);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      setIsDesktop(false);
      return;
    }
    const media = window.matchMedia("(min-width: 1024px)");
    const updateMode = () => setIsDesktop(media.matches);
    updateMode();
    media.addEventListener("change", updateMode);
    return () => media.removeEventListener("change", updateMode);
  }, []);

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger || !isDesktop) return;
      const rect = trigger.getBoundingClientRect();
      const width = Math.min(420, window.innerWidth - 24);
      setDesktopPosition({
        left: Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12)),
        top: rect.bottom + 8,
        width,
        maxHeight: Math.max(220, window.innerHeight - rect.bottom - 20),
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isDesktop, open, triggerRef]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    if (!isDesktop) document.body.style.overflow = "hidden";
    window.setTimeout(() => {
      panelRef.current
        ?.querySelector<HTMLElement>("button, input, [role='button']")
        ?.focus();
    }, 0);
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDesktop, open]);

  if (!open) return null;

  const trapFocus = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      window.setTimeout(() => triggerRef.current?.focus(), 0);
      return;
    }
    if (event.key !== "Tab" || isDesktop) return;
    const focusable = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(
        "button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])"
      ) ?? []
    ).filter((element) => element.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const panel = (
    <div
      className={isDesktop ? "fixed inset-0 z-[85]" : "fixed inset-0 z-[85] flex items-end justify-center"}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {!isDesktop ? <div className="absolute inset-0 bg-[#02040C]/75 backdrop-blur-sm" /> : null}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal={!isDesktop}
        aria-label={title}
        onKeyDown={trapFocus}
        className={`orbit-filter-panel relative flex w-full flex-col overflow-hidden shadow-2xl ${
          isDesktop ? "fixed max-h-[min(680px,calc(100dvh-90px))] rounded-2xl" : "max-h-[90dvh] max-w-[520px] rounded-t-2xl"
        }`}
        style={isDesktop && desktopPosition ? desktopPosition : undefined}
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <h2 className="orbit-filter-title font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${title}`}
            className="orbit-tab grid h-11 w-11 place-items-center rounded-xl"
          >
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        <div className="orbit-filter-actions grid grid-cols-[0.85fr_1.15fr] gap-2 border-t border-slate-200 p-3">
          <button
            type="button"
            onClick={onReset}
            className="orbit-tab inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold"
          >
            <RotateCcw size={15} />
            Reset
          </button>
          <button
            type="button"
            onClick={onApply}
            className="orbit-button-primary min-h-11 px-4 py-2 text-sm"
          >
            {applyLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
